/**
 * The first components everything else depends on (§11.3), extracted from
 * `Foundations.dc.html` in the order the brief gives.
 *
 * Two rules hold across all of them:
 *   · never colour alone — every state carries a glyph AND a word (§11.7);
 *   · the item-row slot order is fixed on every screen (§11.3), so a reviewer's eye
 *     lands in the same place on the dashboard, in the workspace and in the matrix.
 */

import type { CSSProperties, ReactNode } from 'react';

import type { ItemState, MaturityLevel, Obligation } from '@portal/domain';

import {
  AutoCheckGlyph,
  EvidenceGlyph,
  FreshnessGlyph,
  InheritedGlyph,
  LockGlyph,
  NotApplicableGlyph,
  PartialGlyph,
  PenGlyph,
  PendingGlyph,
  RejectedGlyph,
  VerifiedGlyph,
  WaivedGlyph,
} from './glyphs.js';

/* ── StateChip ─────────────────────────────────────────────────────────────── */

const STATE_PRESENTATION: Record<
  ItemState,
  { label: string; fg: string; bg: string; border: string; dashed?: boolean; Glyph: typeof VerifiedGlyph }
> = {
  VERIFIED: { label: 'Verified', fg: 'var(--ok)', bg: 'var(--okb)', border: 'var(--okbd)', Glyph: VerifiedGlyph },
  PARTIAL: { label: 'Partial', fg: 'var(--wa)', bg: 'var(--wab)', border: 'var(--wabd)', Glyph: PartialGlyph },
  REJECTED: { label: 'Rejected', fg: 'var(--ba)', bg: 'var(--bab)', border: 'var(--babd)', Glyph: RejectedGlyph },
  PENDING: { label: 'Pending', fg: 'var(--ne)', bg: 'var(--neb)', border: 'var(--nebd)', Glyph: PendingGlyph },
  NOT_APPLICABLE: {
    label: 'N/A',
    fg: 'var(--na)',
    bg: 'transparent',
    border: 'var(--nabd)',
    dashed: true,
    Glyph: NotApplicableGlyph,
  },
  WAIVED: { label: 'Waived', fg: 'var(--wv)', bg: 'var(--wvb)', border: 'var(--wvbd)', Glyph: WaivedGlyph },
};

export function stateLabel(state: ItemState): string {
  return STATE_PRESENTATION[state].label;
}

export interface StateChipProps {
  state: ItemState;
  /** The 96px fixed-width form used in the item row's state slot. */
  fixedWidth?: boolean;
  size?: 'sm' | 'md';
}

export function StateChip({ state, fixedWidth = false, size = 'md' }: StateChipProps) {
  const { label, fg, bg, border, dashed, Glyph } = STATE_PRESENTATION[state];
  const height = size === 'sm' ? 19 : 22;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: fixedWidth ? 'center' : undefined,
        gap: 4,
        height,
        width: fixedWidth ? 96 : undefined,
        padding: '0 7px',
        borderRadius: 'var(--r-md)',
        border: `1px ${dashed === true ? 'dashed' : 'solid'} ${border}`,
        background: bg,
        color: fg,
        fontSize: size === 'sm' ? 10.5 : 11,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <Glyph size={size === 'sm' ? 11 : 12} />
      {label}
    </span>
  );
}

/* ── MarkerChip ────────────────────────────────────────────────────────────── */

/**
 * §4.2: `[C]` is a modifier, not a third marker. The chip renders the compound —
 * `[M]`, `[R]`, `[C][M]`, `[C][R]` — which is why it takes two props and not an enum.
 */
export interface MarkerChipProps {
  obligation: Obligation;
  isContextual: boolean;
}

export function MarkerChip({ obligation, isContextual }: MarkerChipProps) {
  const mandatory = obligation === 'MANDATORY';
  const label = `${isContextual ? '[C]' : ''}${mandatory ? '[M]' : '[R]'}`;
  const title = `${isContextual ? 'Contextual — applies only when its condition is true. ' : ''}${
    mandatory
      ? 'Mandatory — counted in the score and blocks the go-live gate.'
      : 'Recommended — reported separately, never blocking.'
  }`;

  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 17,
        minWidth: 25,
        padding: '0 4px',
        borderRadius: 'var(--r-sm)',
        font: `600 9.5px/1 var(--font-mono)`,
        ...(mandatory
          ? { background: '#2a3240', color: '#fff' }
          : {
              background: 'transparent',
              color: 'var(--mu)',
              border: `1px ${isContextual ? 'dashed' : 'solid'} var(--nabd)`,
            }),
      }}
    >
      {label}
    </span>
  );
}

/* ── ProvenanceChip ────────────────────────────────────────────────────────── */

export type Provenance =
  | { kind: 'inherited'; auditNumber: number }
  | { kind: 'auto-pass'; ageLabel: string }
  | { kind: 'auto-fail' }
  | { kind: 'expiring'; daysRemaining: number }
  | { kind: 'expired' }
  | { kind: 'attested'; by: string }
  | { kind: 'read-only'; reason: string };

export function ProvenanceChip({ provenance }: { provenance: Provenance }) {
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    height: 20,
    padding: '0 6px',
    borderRadius: 'var(--r-md)',
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    border: '1px solid',
  };

  switch (provenance.kind) {
    // A dashed border always means "not verified in this audit".
    case 'inherited':
      return (
        <span
          style={{ ...base, borderStyle: 'dashed', borderColor: 'var(--inbd)', background: 'var(--acs)', color: 'var(--ac)' }}
          title={`Inherited from audit #${provenance.auditNumber} — not re-verified in this audit`}
        >
          <InheritedGlyph size={11} />#{provenance.auditNumber}
        </span>
      );
    case 'auto-pass':
      return (
        <span
          style={{ ...base, borderColor: 'var(--okbd)', background: 'var(--okb)', color: 'var(--ok)' }}
          title="A connector probe agrees with the recorded state"
        >
          <AutoCheckGlyph size={11} />Auto pass · {provenance.ageLabel}
        </span>
      );
    case 'auto-fail':
      return (
        <span
          style={{ ...base, borderColor: 'var(--babd)', background: 'var(--bab)', color: 'var(--ba)' }}
          title="The probe diverges from the recorded state — someone must decide, and the decision is logged"
        >
          <AutoCheckGlyph size={11} />Auto fail · diverges
        </span>
      );
    case 'expiring':
      return (
        <span
          style={{ ...base, borderColor: 'var(--wabd)', background: 'var(--wab)', color: 'var(--wa)' }}
          title={`Evidence expires in ${provenance.daysRemaining} days`}
        >
          <FreshnessGlyph size={11} />
          {provenance.daysRemaining} d left
        </span>
      );
    case 'expired':
      return (
        <span
          style={{ ...base, borderColor: 'var(--babd)', background: 'var(--bab)', color: 'var(--ba)' }}
          title="Evidence is outside its freshness window"
        >
          <FreshnessGlyph size={11} />Expired
        </span>
      );
    // Verified on someone's word rather than an artefact. Deliberately visible in the
    // row: the distinction is the point, and hiding it is what makes a compliance
    // number worthless (ADR 0008).
    case 'attested':
      return (
        <span
          style={{ ...base, borderStyle: 'dashed', borderColor: 'var(--nabd)', background: 'transparent', color: 'var(--mu)' }}
          title={`Attested by ${provenance.by} — verified on their confirmation, with no artefact attached`}
        >
          <PenGlyph size={11} />Attested
        </span>
      );
    case 'read-only':
      return (
        <span
          style={{ ...base, borderColor: 'var(--bd)', background: 'var(--sunk)', color: 'var(--mu)' }}
          title={provenance.reason}
        >
          <LockGlyph size={11} />Read-only
        </span>
      );
  }
}

/* ── ScoreRing ─────────────────────────────────────────────────────────────── */

const MATURITY: Record<MaturityLevel, { label: string; colour: string }> = {
  L1: { label: 'L1 — Ad hoc', colour: 'var(--ba)' },
  L2: { label: 'L2 — Partial', colour: 'var(--wa)' },
  L3: { label: 'L3 — Managed', colour: 'var(--ac)' },
  L4: { label: 'L4 — Optimised', colour: 'var(--ok)' },
};

export function maturityLabel(level: MaturityLevel): string {
  return MATURITY[level].label;
}

export interface ScoreRingProps {
  /** 0–100, or null when nothing applicable is in scope — rendered "—", never 0%. */
  percent: number | null;
  size?: number;
  caption?: string;
  maturity?: MaturityLevel | null;
}

export function ScoreRing({ percent, size = 104, caption = 'MANDATORY %', maturity }: ScoreRingProps) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const filled = percent === null ? 0 : (Math.max(0, Math.min(100, percent)) / 100) * circumference;
  const colour = maturity == null ? 'var(--ac)' : MATURITY[maturity].colour;

  return (
    <svg
      viewBox="0 0 110 110"
      width={size}
      height={size}
      role="img"
      aria-label={percent === null ? 'No applicable mandatory items' : `${Math.round(percent)} percent`}
      style={{ flex: '0 0 auto' }}
    >
      <circle cx="55" cy="55" r={radius} fill="none" stroke="var(--sunk)" strokeWidth="11" />
      {percent !== null && (
        <circle
          cx="55"
          cy="55"
          r={radius}
          fill="none"
          stroke={colour}
          strokeWidth="11"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference}`}
          transform="rotate(-90 55 55)"
        />
      )}
      <text
        x="55"
        y="57"
        textAnchor="middle"
        style={{ font: `600 30px var(--font-sans)`, fill: 'var(--tx)' }}
      >
        {percent === null ? '—' : Math.round(percent)}
      </text>
      <text
        x="55"
        y="72"
        textAnchor="middle"
        style={{ font: `500 9px var(--font-sans)`, fill: 'var(--mu)', letterSpacing: '0.05em' }}
      >
        {caption}
      </text>
    </svg>
  );
}

/* ── SectionBar ────────────────────────────────────────────────────────────── */

/**
 * Stacked, because a flat percentage does not say *why* a section is short (§11.3).
 */
export interface SectionBarProps {
  counts: {
    verified: number;
    partial: number;
    rejected: number;
    waived: number;
    pending: number;
  };
  height?: number;
}

export function SectionBar({ counts, height = 8 }: SectionBarProps) {
  const total =
    counts.verified + counts.partial + counts.rejected + counts.waived + counts.pending;

  const segments: Array<[number, string, string]> = [
    [counts.verified, 'var(--ok)', 'verified'],
    [counts.partial, 'var(--wa)', 'partial'],
    [counts.rejected, 'var(--ba)', 'rejected'],
    [counts.waived, 'var(--wv)', 'waived'],
  ];

  return (
    <div
      style={{
        flex: 1,
        height,
        borderRadius: height / 2,
        background: 'var(--sunk)',
        overflow: 'hidden',
        display: 'flex',
      }}
      role="img"
      aria-label={
        total === 0
          ? 'No applicable items'
          : segments.map(([count, , name]) => `${count} ${name}`).join(', ')
      }
    >
      {total > 0 &&
        segments.map(([count, colour, name]) =>
          count === 0 ? null : (
            <div key={name} style={{ width: `${(count / total) * 100}%`, background: colour }} />
          ),
        )}
    </div>
  );
}

/* ── Avatar ────────────────────────────────────────────────────────────────── */

const AVATAR_COLOURS: Record<string, string> = {
  SB: 'var(--av-sb)',
  JR: 'var(--av-jr)',
  MK: 'var(--av-mk)',
  AP: 'var(--av-ap)',
  LN: 'var(--av-ln)',
  CF: 'var(--av-cf)',
};

export function Avatar({
  initials,
  name,
  size = 20,
}: {
  initials: string;
  name?: string;
  size?: number;
}) {
  return (
    <span
      title={name ?? initials}
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: '50%',
        background: AVATAR_COLOURS[initials] ?? 'var(--av-system)',
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        font: `600 ${Math.round(size * 0.45)}px/1 var(--font-sans)`,
      }}
    >
      {initials}
    </span>
  );
}

/* ── ItemRow ───────────────────────────────────────────────────────────────── */

/**
 * The fixed 8-slot anatomy (§11.3): ref · marker · statement · provenance · roll-up ·
 * evidence count · owner · state. 38px tall. The statement truncates; it never wraps.
 */
export interface ItemRowProps {
  ref_: string;
  obligation: Obligation;
  isContextual: boolean;
  statement: string;
  state: ItemState;
  provenance?: Provenance[];
  rollUp?: { verified: number; considered: number; state: ItemState } | null;
  evidenceCount?: number;
  owner?: { initials: string; displayName: string } | null;
  selected?: boolean;
  onSelect?: () => void;
  id?: string;
}

export function ItemRow({
  ref_,
  obligation,
  isContextual,
  statement,
  state,
  provenance = [],
  rollUp = null,
  evidenceCount = 0,
  owner = null,
  selected = false,
  onSelect,
  id,
}: ItemRowProps) {
  return (
    <div
      id={id}
      role="option"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        height: 'var(--h-row)',
        padding: '0 14px',
        borderBottom: '1px solid var(--bd)',
        background: selected ? 'var(--acs)' : 'var(--sf)',
        boxShadow: selected ? 'inset 2px 0 0 var(--ac)' : undefined,
        cursor: onSelect === undefined ? undefined : 'default',
      }}
    >
      <span
        className="mono"
        style={{ width: 34, flex: '0 0 34px', fontSize: 11.5, fontWeight: 600, color: 'var(--mu)' }}
      >
        {ref_}
      </span>

      <MarkerChip obligation={obligation} isContextual={isContextual} />

      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 'var(--fs-row)',
          fontWeight: selected ? 600 : 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={statement}
      >
        {statement}
      </span>

      {provenance.map((entry, index) => (
        <ProvenanceChip key={`${entry.kind}-${index}`} provenance={entry} />
      ))}

      {rollUp !== null && (
        <StateChip state={rollUp.state} size="sm" />
      )}
      {rollUp !== null && (
        <span className="mono" style={{ fontSize: 11, color: 'var(--mu)' }}>
          {rollUp.verified}/{rollUp.considered} repos
        </span>
      )}

      {evidenceCount > 0 && (
        <span
          style={{ display: 'flex', alignItems: 'center', gap: 3, color: 'var(--mu)', fontSize: 11 }}
          title={`${evidenceCount} evidence record${evidenceCount === 1 ? '' : 's'}`}
        >
          <EvidenceGlyph size={12} />
          {evidenceCount}
        </span>
      )}

      {owner !== null && <Avatar initials={owner.initials} name={owner.displayName} />}

      <StateChip state={state} fixedWidth />
    </div>
  );
}

/* ── PermissionButton ──────────────────────────────────────────────────────── */

/**
 * §8.4 — disabled controls are **shown, greyed, with a lock and a reason on hover**,
 * never hidden. Baked in here so it cannot be forgotten per screen: an auditor who
 * cannot see the Approve button assumes the tool is broken; one who sees it greyed with
 * "Only the Delivery Manager can approve" understands the system.
 */
export interface PermissionButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'destructive';
  /** Non-null disables the button and becomes its tooltip. */
  disabledReason?: string | null;
  type?: 'button' | 'submit';
}

export function PermissionButton({
  children,
  onClick,
  variant = 'secondary',
  disabledReason = null,
  type = 'button',
}: PermissionButtonProps) {
  const disabled = disabledReason !== null;

  const palette: Record<string, CSSProperties> = {
    primary: { background: 'var(--ac)', borderColor: 'var(--ac)', color: '#fff' },
    secondary: { background: 'var(--sf)', borderColor: 'var(--bd)', color: 'var(--tx)' },
    destructive: { background: 'var(--ba)', borderColor: 'var(--ba)', color: '#fff' },
  };

  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabledReason ?? undefined}
      aria-disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 'var(--h-control)',
        padding: '0 12px',
        borderRadius: 'var(--r-lg)',
        border: '1px solid',
        font: `500 12.5px/1 var(--font-sans)`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        ...palette[variant],
      }}
    >
      {disabled && <LockGlyph size={12} />}
      {children}
    </button>
  );
}
